import type { QueryRowType } from "@rocicorp/zero";
import { styled } from "goober";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { isConfigured } from "../../../shared/connectors/isDraft";
import { REQUEST_CONNECTOR_SHARE_FLAG } from "../../../shared/featureFlagNames";
import { queries } from "../../../shared/queries";
import { config } from "../../environment";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { useQuery } from "../../hooks/useQuery";
import { useUrlSort } from "../../hooks/useUrlSort";
import {
  type Connector,
  connectorGroupKey,
  connectorAuthTypeLabel,
  connectorMatchesQuery,
  connectorTypeLabel,
  resolveProduct,
} from "../../lib/connector";
import { getWritableScopeIds } from "../../lib/getWritableScopeIds";
import { pluralize } from "../../lib/inflection";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { userDisplayName } from "../../lib/userDisplayName";
import { ConnectorStatusPill } from "../ConnectorStatusPill";
import { Avatar } from "../Avatar";
import { Button } from "../Button";
import { ButtonPress } from "../Button/ButtonPress";
import { ConnectorLogo } from "../ConnectorLogo";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { ConnectorUsageIcons } from "../ConnectorUsageIcons";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import {
  type FilterDef,
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
  toggleMultiFilter,
} from "../ListFilter";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { StatusBadge } from "../StatusBadge";
import { StatusDot } from "../StatusDot";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import { MainScrollContainer } from "../MainScrollContainer";
import { DeleteConnectorModalBody } from "./DeleteConnectorModal";

function NoteCell({ note }: { note: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [truncated, setTruncated] = useState(false);

  const content = (
    <div
      ref={ref}
      onMouseEnter={() => {
        const el = ref.current?.firstElementChild;
        if (el) setTruncated(el.scrollHeight > el.clientHeight);
      }}
    >
      <Text
        variant="text-small"
        color="var(--text-secondary)"
        style={{
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          overflowWrap: "anywhere",
        }}
      >
        {note}
      </Text>
    </div>
  );

  if (!truncated) return content;

  return (
    <Tooltip content={note} align="left">
      {content}
    </Tooltip>
  );
}

type ConnectorRow = QueryRowType<ReturnType<typeof queries.connectedAppsIndex>>;
type AppCreator = NonNullable<ConnectorRow["createdBy"]>;

const appStatusOrder = ["active", "untested", "error", "draft"] as const;
type AppStatusValue = (typeof appStatusOrder)[number];

const appStatusLabels: Record<AppStatusValue, string> = {
  active: "Active",
  untested: "Untested",
  error: "Error",
  draft: "Draft",
};

const appStatusColors: Record<AppStatusValue, string> = {
  active: "green",
  untested: "gray",
  error: "red",
  draft: "orange",
};

function getAppStatus(app: Connector): AppStatusValue {
  if (!isConfigured(app)) return "draft";
  if (app.lastTestStatus === "failure") return "error";
  if (!app.lastTestStatus || app.lastTestStatus === "pending")
    return "untested";
  return "active";
}

function AppStatusDot({
  status,
  size,
}: {
  status: AppStatusValue;
  size: number;
}) {
  return <StatusDot color={appStatusColors[status]} size={size} />;
}

const LogoStack = styled("div")`
  position: relative;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`;

const StackTile = styled("div")`
  position: absolute;
  inset: 0;
  border-radius: 8px;
  background: var(--alpha8);
  transform-origin: bottom center;
  transition: transform 0.12s ease-out;
`;

const LogoFront = styled(StackTile)`
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface-secondary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  transform-origin: center;
`;

const GroupTag = styled("span")`
  display: inline-flex;
  align-items: center;
  height: 16px;
  padding: 0 6px;
  border-radius: 8px;
  background: var(--alpha8);
  color: var(--text-secondary);
  flex-shrink: 0;
`;

const statusSummaryOrder: AppStatusValue[] = [
  "error",
  "untested",
  "active",
  "draft",
];

const StatusSummaryPill = styled("div")`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;
  padding: 6px 8px;
  background: var(--surface-primary);
  border: 0.5px solid var(--border-alpha);
  border-radius: 16px;
`;

function GroupStatusSummary({ members }: { members: ConnectorRow[] }) {
  const counts = new Map<AppStatusValue, number>();
  for (const member of members) {
    const status = getAppStatus(member);
    counts.set(status, (counts.get(status) ?? 0) + 1);
  }
  const present = statusSummaryOrder.filter((status) => counts.has(status));
  const worst = present[0];
  if (!worst) return null;
  return (
    <Tooltip
      content={present
        .map((status) => `${counts.get(status)} ${appStatusLabels[status]}`)
        .join(", ")}
    >
      <StatusSummaryPill>
        <AppStatusDot status={worst} size={12} />
        <Text variant="text-small-md" color="var(--text-primary)">
          {members.length}
        </Text>
      </StatusSummaryPill>
    </Tooltip>
  );
}

export function Connected({ active = true }: { active?: boolean }) {
  const [connectorsResult, connectorsDetails] = useQuery(
    queries.connectedAppsIndex()
  );
  const flagOn = useFeatureFlag(REQUEST_CONNECTOR_SHARE_FLAG) === true;
  const [shareRequestsRaw] = useQuery(queries.pendingGrantRequests());
  const [currentUser] = useQuery(queries.currentUser());
  // The pending-approvals group shows requests the current user can review; the
  // query already scopes results to reviewable + own, so anything that isn't
  // their own is one they can act on.
  const shareRequests = useMemo(
    () =>
      flagOn && currentUser
        ? shareRequestsRaw.filter(
            (r) =>
              r.scopeAsConnector != null &&
              r.requestedByUserId !== currentUser.id
          )
        : [],
    [flagOn, currentUser, shareRequestsRaw]
  );
  // While a connector has a pending request it lives in the pending group, so
  // keep it out of the main list to avoid showing it in two places.
  const pendingConnectorIds = useMemo(
    () => new Set(shareRequests.map((r) => r.scopeId)),
    [shareRequests]
  );
  // The current user's own pending requests — their connectors stay in the list,
  // flagged "Awaiting approval" so the requester can track them.
  const myPendingConnectorIds = useMemo(
    () =>
      new Set(
        flagOn && currentUser
          ? shareRequestsRaw
              .filter((r) => r.requestedByUserId === currentUser.id)
              .map((r) => r.scopeId)
          : []
      ),
    [flagOn, currentUser, shareRequestsRaw]
  );
  const [requestsExpanded, setRequestsExpanded] = useState(false);
  const {
    q: query,
    status: statusParam,
    creator: creatorParam,
  } = useCurrentUrl(routes.connectors.connected);
  const statusFilter: AppStatusValue | null = (
    appStatusOrder as readonly string[]
  ).includes(statusParam)
    ? (statusParam as AppStatusValue)
    : null;
  const creatorFilters = useMemo(
    () => parseMultiFilter(creatorParam),
    [creatorParam]
  );
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.connectors.connected,
    {
      name: "asc",
      usedBy: "asc",
      addedBy: "asc",
      createdAt: "desc",
    }
  );
  const [confirmDeleteApp, setConfirmDeleteApp] = useState<{
    id: string;
    oauthConnectionId?: string;
  } | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set()
  );

  const [myGrants] = useQuery(queries.currentUserGrants());
  const writableAppIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants ?? [],
        connectorsResult.map((app) => app.id),
        "connected_app:write"
      ),
    [myGrants, connectorsResult]
  );

  const queryFilteredApps = useMemo(() => {
    if (!query) return connectorsResult;
    const lowerQuery = query.toLowerCase();
    return connectorsResult.filter((app) =>
      connectorMatchesQuery(app, lowerQuery)
    );
  }, [connectorsResult, query]);

  const availableStatuses = new Set<AppStatusValue>();
  const availableCreators = new Map<string, AppCreator>();
  for (const app of queryFilteredApps) {
    availableStatuses.add(getAppStatus(app));
    if (app.createdByUserId && app.createdBy) {
      availableCreators.set(app.createdByUserId, app.createdBy);
    }
  }
  const availableStatusOptions = appStatusOrder.filter((s) =>
    availableStatuses.has(s)
  );
  const availableCreatorOptions = [...availableCreators.values()].sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );

  const connectors = useMemo(
    () =>
      queryFilteredApps.filter((app) => {
        if (pendingConnectorIds.has(app.id)) return false;
        if (statusFilter !== null && getAppStatus(app) !== statusFilter)
          return false;
        if (creatorFilters.length > 0) {
          if (
            !app.createdByUserId ||
            !creatorFilters.includes(app.createdByUserId)
          )
            return false;
        }
        return true;
      }),
    [queryFilteredApps, pendingConnectorIds, statusFilter, creatorFilters]
  );

  const filters: FilterDef[] = [
    {
      kind: "single",
      key: "status",
      label: "Status",
      icon: <Icon name="circle-dashed" size={16} />,
      options: availableStatusOptions.map((option) => ({
        id: option,
        label: appStatusLabels[option],
        menuLeft: <AppStatusDot status={option} size={12} />,
        pillLeft: <AppStatusDot status={option} size={10} />,
      })),
      value: statusFilter,
      onSelect: (id) => navigate(null, { status: id }, { replace: true }),
      onClear: () => navigate(null, { status: "all" }, { replace: true }),
    },
    {
      kind: "multi",
      key: "creator",
      label: "Added by",
      icon: <Icon name="circle-user-round" size={16} />,
      options: availableCreatorOptions.map((creator) => ({
        id: creator.id,
        label: userDisplayName(creator),
        menuLeft: <Avatar user={creator} size={16} />,
        pillLeft: <Avatar user={creator} size={12} />,
      })),
      values: creatorFilters,
      onToggle: (id) =>
        navigate(
          null,
          { creator: toggleMultiFilter(creatorFilters, id) },
          { replace: true }
        ),
    },
  ];

  const compareApps = useCallback(
    (a: ConnectorRow, b: ConnectorRow) => {
      if (!sortColumn) return b.createdAt - a.createdAt;

      let aVal: string | number;
      let bVal: string | number;

      switch (sortColumn) {
        case "name":
          aVal = a.name.toLowerCase();
          bVal = b.name.toLowerCase();
          break;
        case "usedBy":
          aVal = a.usages.length;
          bVal = b.usages.length;
          break;
        case "addedBy":
          aVal = a.createdBy ? userDisplayName(a.createdBy).toLowerCase() : "";
          bVal = b.createdBy ? userDisplayName(b.createdBy).toLowerCase() : "";
          break;
        case "createdAt":
          aVal = a.createdAt;
          bVal = b.createdAt;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      return 0;
    },
    [sortColumn, sortDirection]
  );

  const groups = useMemo(() => {
    const sortedApps = [...connectors].sort(compareApps);
    const groupsByKey = new Map<
      string,
      { key: string; label: string; rep: ConnectorRow; members: ConnectorRow[] }
    >();
    for (const app of sortedApps) {
      const key = connectorGroupKey(app);
      const group = groupsByKey.get(key);
      if (group) group.members.push(app);
      else
        groupsByKey.set(key, {
          key,
          label: connectorTypeLabel(app),
          rep: app,
          members: [app],
        });
    }
    return [...groupsByKey.values()].sort((a, b) => {
      if (sortColumn && sortColumn !== "name") return compareApps(a.rep, b.rep);
      const cmp = a.label.localeCompare(b.label);
      return sortColumn === "name" && sortDirection === "desc" ? -cmp : cmp;
    });
  }, [connectors, compareApps, sortColumn, sortDirection]);

  const rollupKeys = useMemo(
    () => groups.filter((g) => g.members.length > 1).map((g) => g.key),
    [groups]
  );
  useEffect(() => {
    setExpandedGroups((prev) => {
      const pruned = new Set([...prev].filter((k) => rollupKeys.includes(k)));
      return pruned.size === prev.size ? prev : pruned;
    });
  }, [rollupKeys]);
  const searchActive = !!query;
  const allExpanded =
    rollupKeys.length > 0 && rollupKeys.every((key) => expandedGroups.has(key));
  const toggleGroup = useCallback(
    (key: string) =>
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      }),
    []
  );
  const toggleAll = () =>
    setExpandedGroups(allExpanded ? new Set() : new Set(rollupKeys));

  const renderConnectorRow = useCallback(
    (app: ConnectorRow, reveal?: { collapsed: boolean; index: number }) => ({
      key: app.id,
      link: makeUrl(routes.modal.connector, { id: app.id }),
      collapsed: reveal?.collapsed,
      revealIndex: reveal?.index,
      name: (hover: boolean) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            minWidth: 0,
          }}
        >
          <ConnectorLogoContainer
            size={32}
            hover={hover}
            borderRadius={8}
            outline={false}
            hoverBackground={false}
          >
            <ConnectorLogo app={app} size={16} />
          </ConnectorLogoContainer>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              minWidth: 0,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                minWidth: 0,
              }}
            >
              <Text
                ellipsis
                variant="text-regular-md"
                color="var(--text-primary)"
              >
                {app.name}
              </Text>
              {myPendingConnectorIds.has(app.id) && (
                <StatusBadge color="orange">Awaiting approval</StatusBadge>
              )}
            </div>
            <Text ellipsis variant="text-small" color="var(--text-secondary)">
              {connectorAuthTypeLabel(app)}
            </Text>
          </div>
        </div>
      ),
      note: app.note ? <NoteCell note={app.note} /> : null,
      status: <ConnectorStatusPill app={app} />,
      usedBy:
        app.usages.length > 0 ? (
          <ConnectorUsageIcons usages={app.usages} />
        ) : (
          <Text variant="text-small">—</Text>
        ),
      addedBy: (
        <Text variant="text-small" color="var(--text-primary)" ellipsis>
          {app.createdBy ? userDisplayName(app.createdBy) : undefined}
        </Text>
      ),
      createdAt: (
        <Text variant="text-small">
          {new Date(app.createdAt).toLocaleDateString()}
        </Text>
      ),
      actions: (hasMouse: boolean) => {
        const canWrite = writableAppIds?.has(app.id) ?? false;
        return (
          <div style={{ marginLeft: "auto", opacity: hasMouse ? 1 : 0 }}>
            <Dropdown
              opener={
                <Button variant="secondary" icon="more" disabled={!canWrite} />
              }
              minWidth={160}
              sections={[
                {
                  items: [
                    {
                      type: "button",
                      left: <Icon name="pencil" size={16} />,
                      label: "Edit",
                      onClick: () =>
                        navigate(routes.modal.connector, { id: app.id }),
                    },
                    {
                      type: "button",
                      left: <Icon name="trash" size={16} />,
                      label: "Delete",
                      onClick: () => {
                        setConfirmDeleteApp({
                          id: app.id,
                          oauthConnectionId: app.oauthConnection?.id,
                        });
                      },
                    },
                  ],
                },
              ]}
            />
          </div>
        );
      },
    }),
    [myPendingConnectorIds, writableAppIds]
  );

  const pendingItems = useMemo(
    () =>
      shareRequests.length > 0
        ? [
            {
              key: "pending-requests-header",
              onClick: () => setRequestsExpanded((v) => !v),
              name: (hover: boolean) => (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    minWidth: 0,
                  }}
                >
                  <LogoStack>
                    <StackTile
                      style={{
                        transform: hover
                          ? "translate(1px, 2px) rotate(-20deg)"
                          : "translate(1px, 2px) rotate(-14deg)",
                      }}
                    />
                    <StackTile
                      style={{
                        transform: hover
                          ? "translate(-1px, 4px) rotate(20deg)"
                          : "translate(-1px, 4px) rotate(14deg)",
                      }}
                    />
                    <LogoFront
                      style={{ transform: hover ? "scale(1.08)" : undefined }}
                    >
                      <Icon
                        name="heart-handshake"
                        size={16}
                        color="var(--text-primary)"
                      />
                    </LogoFront>
                  </LogoStack>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        minWidth: 0,
                      }}
                    >
                      <Text
                        ellipsis
                        variant="text-regular-md"
                        color="var(--text-primary)"
                      >
                        Pending approvals
                      </Text>
                      <StatusBadge color="orange">
                        {`${shareRequests.length} ${pluralize(
                          "request",
                          shareRequests.length
                        )}`}
                      </StatusBadge>
                    </div>
                    <Text
                      ellipsis
                      variant="text-small"
                      color="var(--text-secondary)"
                    >
                      Awaiting your approval
                    </Text>
                  </div>
                </div>
              ),
              note: null,
              status: null,
              usedBy: null,
              addedBy: null,
              createdAt: null,
              actions: (
                <div
                  style={{
                    marginLeft: "auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 24,
                    height: 24,
                  }}
                >
                  <Icon
                    name="chevron-right"
                    size={16}
                    color="var(--text-secondary)"
                    style={{
                      transition: "transform 0.15s ease-out",
                      transform: requestsExpanded ? "rotate(90deg)" : undefined,
                    }}
                  />
                </div>
              ),
            },
            ...shareRequests.map((r, i) => {
              const connector = r.scopeAsConnector!;
              const requester = r.requestedByUser
                ? userDisplayName(r.requestedByUser)
                : "Someone";
              return {
                key: `request:${r.id}`,
                link: makeUrl(routes.modal.connector, {
                  id: r.scopeId,
                  tab: "access",
                }),
                collapsed: !requestsExpanded,
                revealIndex: i,
                name: (hover: boolean) => (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      minWidth: 0,
                    }}
                  >
                    <ConnectorLogoContainer
                      size={32}
                      hover={hover}
                      borderRadius={8}
                      outline={false}
                      hoverBackground={false}
                    >
                      <ConnectorLogo app={connector} size={16} />
                    </ConnectorLogoContainer>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        minWidth: 0,
                      }}
                    >
                      <Text
                        ellipsis
                        variant="text-regular-md"
                        color="var(--text-primary)"
                      >
                        {connector.name}
                      </Text>
                      <Text
                        ellipsis
                        variant="text-small"
                        color="var(--text-secondary)"
                      >
                        {`Requested by ${requester}`}
                      </Text>
                    </div>
                  </div>
                ),
                note: null,
                status: null,
                usedBy: null,
                addedBy: null,
                createdAt: (
                  <Text variant="text-small">
                    {new Date(r.createdAt).toLocaleDateString()}
                  </Text>
                ),
                actions: null,
              };
            }),
          ]
        : [],
    [shareRequests, requestsExpanded]
  );

  // Search results render flat: every row is a match, and a member's type
  // label already says what a group header would.
  const groupItems = useMemo(
    () =>
      groups.flatMap((group) => {
        if (group.members.length === 1 || searchActive) {
          return group.members.map((member) => renderConnectorRow(member));
        }
        const expanded = expandedGroups.has(group.key);
        const count = group.members.length;
        const repProduct = resolveProduct(group.rep);
        const header = {
          key: `group:${group.key}`,
          onClick: () => toggleGroup(group.key),
          name: (hover: boolean) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                minWidth: 0,
              }}
            >
              <LogoStack>
                <StackTile
                  style={{
                    transform: hover
                      ? "translate(1px, 2px) rotate(-20deg)"
                      : "translate(1px, 2px) rotate(-14deg)",
                  }}
                />
                <StackTile
                  style={{
                    transform: hover
                      ? "translate(-1px, 4px) rotate(20deg)"
                      : "translate(-1px, 4px) rotate(14deg)",
                  }}
                />
                <LogoFront
                  style={{ transform: hover ? "scale(1.08)" : undefined }}
                >
                  {repProduct ? (
                    <ConnectorLogo
                      template={{ name: group.label, identifier: repProduct }}
                      size={16}
                    />
                  ) : (
                    <ConnectorLogo app={group.rep} size={16} />
                  )}
                </LogoFront>
              </LogoStack>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    minWidth: 0,
                  }}
                >
                  <Text
                    ellipsis
                    variant="text-regular-md"
                    color="var(--text-primary)"
                  >
                    {group.label}
                  </Text>
                  <GroupTag>
                    <Text variant="text-smaller-md">
                      {`${count} ${pluralize("connector", count)}`}
                    </Text>
                  </GroupTag>
                </div>
                <Text
                  ellipsis
                  variant="text-small"
                  color="var(--text-secondary)"
                >
                  {connectorTypeLabel(group.rep)}
                </Text>
              </div>
            </div>
          ),
          note: null,
          status: <GroupStatusSummary members={group.members} />,
          usedBy: null,
          addedBy: null,
          createdAt: null,
          actions: (
            <div
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
              }}
            >
              <Icon
                name="chevron-right"
                size={16}
                color="var(--text-secondary)"
                style={{
                  transition: "transform 0.15s ease-out",
                  transform: expanded ? "rotate(90deg)" : undefined,
                }}
              />
            </div>
          ),
        };
        return [
          header,
          ...group.members.map((member, i) =>
            renderConnectorRow(member, { collapsed: !expanded, index: i })
          ),
        ];
      }),
    [groups, searchActive, expandedGroups, renderConnectorRow, toggleGroup]
  );

  const items = useMemo(
    () => [...pendingItems, ...groupItems],
    [pendingItems, groupItems]
  );

  return (
    <>
      {active && <title>{`Connectors • ${config.title}`}</title>}
      <MainScrollContainer>
        <ListTable
          style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
          columns={{ note: "md" }}
          footer={
            hasActiveFilters(filters) ? (
              <ListFilterPills filters={filters} />
            ) : null
          }
          headerActions={
            <>
              {rollupKeys.length > 0 && !searchActive ? (
                <Tooltip content={allExpanded ? "Collapse all" : "Expand all"}>
                  <ButtonPress
                    variant="secondary"
                    icon={allExpanded ? "chevron-up" : "chevron-down"}
                    onPress={toggleAll}
                  />
                </Tooltip>
              ) : null}
              <ListFilter filters={filters} />
            </>
          }
          emptyState={
            connectorsDetails.type === "complete" ? (
              connectorsResult.length > 0 ? (
                <EmptyState
                  iconName="apps-empty"
                  title="No matches"
                  description="Try a different filter."
                />
              ) : !query ? (
                <EmptyState
                  iconName="apps-empty"
                  title="No connectors"
                  description="Add a connector from the library to use it in workflows."
                />
              ) : undefined
            ) : undefined
          }
          headers={{
            name: "Connector name",
            note: "Note",
            status: "Status",
            usedBy: "Used by",
            addedBy: "Added by",
            createdAt: "Created at",
            actions: "",
          }}
          sortableColumns={sortableColumns}
          dateColumns={["createdAt"]}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          items={items}
        />
      </MainScrollContainer>
      <Modal
        open={confirmDeleteApp !== null}
        onClose={() => setConfirmDeleteApp(null)}
      >
        {confirmDeleteApp && (
          <DeleteConnectorModalBody
            appId={confirmDeleteApp.id}
            onClose={() => setConfirmDeleteApp(null)}
          />
        )}
      </Modal>
    </>
  );
}
